// Merchant inventory CRUD.
//
// Lists own items grouped by category, with an inline "available" toggle,
// edit/delete row actions, and an Add modal at top-right.
//
// Real S3 image upload lands in Phase 1.5. Until then the icon picker
// uses a small whitelist of lucide names so merchants can at least
// pick something visual.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Pencil, Trash2, Eye, EyeOff, ShoppingBag, LayoutDashboard, Package,
  Inbox, Banknote, Settings, Loader2, Upload, Image as ImageIcon, X, LayoutGrid,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  catalog, merchantItems, uploadImage, type MerchantItem, type MerchantItemInput,
  type ServiceCategory,
} from '../../lib/api';
import { PortalShell, type NavSection } from '../../components/shared/PortalShell';
import { Card, SectionHeader, EmptyState } from '../../components/shared/primitives';
import { iconFor } from '../../lib/icons';
import { formatHtgMinor } from '../../lib/money';
import { Button } from '../../components/stripe-ui/Button';
import { CatalogBrowser } from './CatalogBrowser';
import { Modal } from '../../components/shared/Modal';

const MERCHANT_NAV: NavSection[] = [
  { items: [
    { id: 'dashboard',  label: 'Dashboard',      icon: LayoutDashboard, href: '/merchant' },
    { id: 'inbox',      label: 'Quote requests', icon: Inbox,           href: '/merchant/quote-requests' },
    { id: 'orders',     label: 'Active orders',  icon: Package,         href: '/merchant/orders' },
    { id: 'inventory',  label: 'Inventory',      icon: ShoppingBag,     href: '/merchant/inventory' },
  ]},
  { title: 'Payouts', items: [
    { id: 'payouts',    label: 'Payouts',        icon: Banknote,        href: '/merchant/payouts' },
  ]},
  { title: 'Account', items: [
    { id: 'settings',   label: 'Settings',       icon: Settings,        href: '/merchant/settings/profile' },
  ]},
];

// Icon names recognized by lib/icons.ts. Keep narrow so the picker is short.
const ICON_CHOICES = [
  'Wheat', 'Droplet', 'Candy', 'Bean', 'EggFried', 'Utensils', 'CircleDot',
  'Onion', 'Sprout', 'Banana', 'CupSoda', 'Snowflake', 'Coffee',
  'Pill', 'Beaker', 'SprayCan', 'BandageIcon',
];

// Canonical set of units a merchant can stock by. Free-text invites
// drift ("Lb." vs "lbs" vs "1 lb"); the dropdown keeps the storefront
// + cart math tidy. "Other" routed to free-text in a follow-up.
const UNIT_CHOICES = [
  { value: 'each',   label: 'each' },
  { value: 'pair',   label: 'pair' },
  { value: 'pack',   label: 'pack' },
  { value: 'dozen',  label: 'dozen' },
  { value: 'box',    label: 'box' },
  { value: 'bag',    label: 'bag' },
  { value: 'bottle', label: 'bottle' },
  { value: 'tin',    label: 'tin / can' },
  { value: 'tube',   label: 'tube' },
  { value: 'loaf',   label: 'loaf' },
  { value: 'bunch',  label: 'bunch' },
  // Weight
  { value: '1 lb',   label: '1 lb' },
  { value: '2 lb',   label: '2 lb' },
  { value: '5 lb',   label: '5 lb' },
  { value: '10 lb',  label: '10 lb' },
  { value: '1 kg',   label: '1 kg' },
  // Volume
  { value: '250 ml', label: '250 ml' },
  { value: '500 ml', label: '500 ml' },
  { value: '1 L',    label: '1 L' },
  { value: '2 L',    label: '2 L' },
  { value: '1 gal',  label: '1 gal' },
  { value: '5 gal',  label: '5 gal' },
  // Count
  { value: '12 ct',  label: '12 ct' },
  { value: '20 ct',  label: '20 ct' },
  { value: '30 ct',  label: '30 ct' },
];

export function InventoryPage() {
  const qc = useQueryClient();
  const itemsQ = useQuery({ queryKey: ['merchant', 'items'], queryFn: merchantItems.listOwn });
  const catsQ  = useQuery({ queryKey: ['catalog', 'categories'], queryFn: catalog.categories });

  const [editing, setEditing] = useState<MerchantItem | null>(null);
  const [adding, setAdding]   = useState(false);
  const [browsing, setBrowsing] = useState(false);

  // Group items by category for the table layout.
  const byCategory = useMemo(() => {
    const map = new Map<number, { category: ServiceCategory; items: MerchantItem[] }>();
    for (const it of itemsQ.data ?? []) {
      const cat = (catsQ.data ?? []).find((c) => c.id === it.category_id);
      if (!cat) continue;
      if (!map.has(cat.id)) map.set(cat.id, { category: cat, items: [] });
      map.get(cat.id)!.items.push(it);
    }
    return Array.from(map.values()).sort((a, b) => a.category.label.localeCompare(b.category.label));
  }, [itemsQ.data, catsQ.data]);

  // Inline availability toggle.
  const toggleMut = useMutation({
    mutationFn: ({ id, available }: { id: number; available: boolean }) => merchantItems.update(id, { available }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merchant', 'items'] }),
    onError: () => toast.error('Could not update availability'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => merchantItems.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant', 'items'] }); toast.success('Item removed'); },
    onError: () => toast.error('Could not delete item'),
  });

  return (
    <PortalShell
      nav={MERCHANT_NAV}
      activeId="inventory"
      title="Inventory"
      subtitle="What you stock and what it costs. Customers shop from this list."
      actions={
        <div className="flex items-center gap-2">
          <Button tone="outline" onClick={() => setBrowsing(true)}>
            <LayoutGrid className="w-3.5 h-3.5 mr-1" /> Add from catalog
          </Button>
          <Button onClick={() => setAdding(true)}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add item
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {itemsQ.isLoading && (
          <div className="text-center py-16 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading inventory…</div>
        )}

        {!itemsQ.isLoading && byCategory.length === 0 && (
          <Card>
            <EmptyState
              icon={ShoppingBag}
              title="No items yet"
              body="Add your first item so customers can shop from your stock. You can stock more anytime."
              action={<Button onClick={() => setAdding(true)}><Plus className="w-3.5 h-3.5 mr-1" /> Add first item</Button>}
            />
          </Card>
        )}

        {byCategory.map(({ category, items }) => (
          <Card key={category.id} padding="p-0">
            <div className="p-5 border-b border-slate-100">
              <SectionHeader
                title={category.label}
                subtitle={`${items.length} item${items.length === 1 ? '' : 's'} · ${items.filter((i) => i.available).length} available`}
              />
            </div>
            <table className="text-[12.5px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
              <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
                <tr>
                  <th className="text-left py-2.5 px-5 font-medium">Item</th>
                  <th className="text-left py-2.5 px-3 font-medium">Unit</th>
                  <th className="text-right py-2.5 px-3 font-medium">Price (HTG)</th>
                  <th className="text-center py-2.5 px-3 font-medium">Available</th>
                  <th className="py-2.5 px-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it) => {
                  const Icon = iconFor(it.system_icon);
                  return (
                    <tr key={it.id}>
                      <td className="py-2.5 px-5">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
                            <Icon className="w-4 h-4" />
                          </span>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900">{it.name}</p>
                            {it.description && <p className="text-[11px] text-slate-500 truncate max-w-md">{it.description}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-slate-600">{it.unit}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-slate-900">{formatHtgMinor(it.price_htg_minor, { withSymbol: false })}</td>
                      <td className="py-2.5 px-3 text-center">
                        <button
                          type="button"
                          onClick={() => toggleMut.mutate({ id: it.id, available: !it.available })}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                            it.available
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-slate-100 text-slate-500'
                          }`}
                          aria-pressed={it.available}
                          title={it.available ? 'Click to hide from storefront' : 'Click to show on storefront'}
                        >
                          {it.available ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          {it.available ? 'Available' : 'Hidden'}
                        </button>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setEditing(it)}
                            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Remove "${it.name}" from inventory?`)) deleteMut.mutate(it.id);
                            }}
                            className="p-1.5 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-700"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        ))}
      </div>

      {(adding || editing) && (
        <ItemEditor
          open
          onClose={() => { setAdding(false); setEditing(null); }}
          existing={editing}
          categories={catsQ.data ?? []}
        />
      )}

      {browsing && <CatalogBrowser onClose={() => setBrowsing(false)} />}
    </PortalShell>
  );
}

// ── Add/Edit modal ─────────────────────────────────────────

function ItemEditor({
  open, onClose, existing, categories,
}: {
  open: boolean;
  onClose: () => void;
  existing: MerchantItem | null;
  categories: ServiceCategory[];
}) {
  const qc = useQueryClient();
  const isEdit = !!existing;
  const cartCats = categories.filter((c) => c.mode === 'cart');

  const [categoryId, setCategoryId] = useState<number | ''>(existing?.category_id ?? (cartCats[0]?.id ?? ''));
  const [name,    setName]          = useState(existing?.name ?? '');
  // Snap free-text legacy units to dropdown choice; fall through to 'each'.
  const [unit,    setUnit]          = useState(() => {
    const initial = existing?.unit ?? 'each';
    return UNIT_CHOICES.some((u) => u.value === initial) ? initial : 'each';
  });
  // Price stored as HTG (gourdes, not minor) in the form; we ×100 on save.
  const [priceHtg, setPriceHtg]     = useState(existing ? String(existing.price_htg_minor / 100) : '');
  const [icon,    setIcon]          = useState(existing?.system_icon ?? 'ShoppingBag');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [available, setAvailable]   = useState(existing?.available ?? true);
  // image_key is the R2 object key; imageUrl is the rendered preview URL.
  // On save we persist image_key; the merchant detail / cart UIs render
  // image_key via the public URL (R2 custom domain configured server-side).
  const [imageKey, setImageKey]     = useState<string | null>(existing?.image_key ?? null);
  const [imageUrl, setImageUrl]     = useState<string | null>(null);
  const [uploading, setUploading]   = useState(false);

  const onPickFile = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const { key, public_url } = await uploadImage(file, 'merchant_item');
      setImageKey(key);
      setImageUrl(public_url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const save = useMutation({
    mutationFn: () => {
      const input: MerchantItemInput = {
        category_id:     Number(categoryId),
        name:            name.trim(),
        unit:            unit || 'each',
        price_htg_minor: Math.round(parseFloat(priceHtg || '0') * 100),
        system_icon:     icon || null,
        image_key:       imageKey,
        description:     description.trim() || null,
        available,
      };
      if (isEdit && existing) return merchantItems.update(existing.id, input);
      return merchantItems.create(input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['merchant', 'items'] });
      qc.invalidateQueries({ queryKey: ['storefront'] });  // marketing strip refreshes too
      toast.success(isEdit ? 'Item updated' : 'Item added');
      onClose();
    },
    onError: (e: Error) => toast.error(e.message || 'Save failed'),
  });

  const valid = name.trim() && categoryId && parseFloat(priceHtg) > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? `Edit ${existing!.name}` : 'Add an item'}
      width="md"
      footer={
        <>
          <Button tone="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!valid || save.isPending}>
            {isEdit ? 'Save changes' : 'Add to inventory'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Labeled label="Category">
          <select value={categoryId} onChange={(e) => setCategoryId(Number(e.target.value))} className={selectCls}>
            <option value="">Select…</option>
            {cartCats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </Labeled>
        <Labeled label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="e.g. Rice (long grain)" />
        </Labeled>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Unit">
            <select value={unit} onChange={(e) => setUnit(e.target.value)} className={selectCls}>
              {UNIT_CHOICES.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
            </select>
          </Labeled>
          <Labeled label="Price (HTG gourdes)" hint="Stored as centimes internally.">
            <input type="number" min="0" step="0.01" value={priceHtg} onChange={(e) => setPriceHtg(e.target.value)} className={inputCls} />
          </Labeled>
        </div>
        <Labeled label="Description (optional)">
          <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} placeholder="A short description shown to shoppers." />
        </Labeled>

        {/* Image upload + preview. If image_key is set we render the public_url
            preview; otherwise the icon picker is the visual fallback. */}
        <Labeled label="Photo" hint="JPG / PNG / WebP / GIF · max 8 MB · falls back to the icon below if empty.">
          <div className="flex items-start gap-3">
            <div className="w-24 h-24 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
              {imageUrl || (imageKey && existing?.image_key === imageKey) ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={imageUrl ?? `${import.meta.env.VITE_R2_PUBLIC_URL ?? ''}/${imageKey}`}
                  alt={name || 'item'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <ImageIcon className="w-6 h-6 text-slate-300" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer text-[12px] font-semibold text-slate-700">
                <Upload className="w-3.5 h-3.5" />
                {uploading ? 'Uploading…' : (imageKey ? 'Replace photo' : 'Upload photo')}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
                />
              </label>
              {imageKey && (
                <button
                  type="button"
                  onClick={() => { setImageKey(null); setImageUrl(null); }}
                  className="ml-2 inline-flex items-center gap-1 px-3 py-2 rounded-md text-[12px] text-rose-700 hover:bg-rose-50"
                >
                  <X className="w-3 h-3" /> Remove
                </button>
              )}
              <p className="mt-1.5 text-[11px] text-slate-500 break-all">
                {imageKey ? `Stored at ${imageKey}` : 'No photo — the icon below will be used.'}
              </p>
            </div>
          </div>
        </Labeled>

        <Labeled label="Icon" hint="Used when no photo is uploaded.">
          <div className="grid grid-cols-9 gap-1.5">
            {ICON_CHOICES.map((name) => {
              const Icon = iconFor(name);
              const active = icon === name;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setIcon(name)}
                  className={`aspect-square rounded-md flex items-center justify-center transition-colors ${
                    active ? 'bg-brand-600 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`}
                  title={name}
                >
                  <Icon className="w-4 h-4" />
                </button>
              );
            })}
          </div>
        </Labeled>
        <label className="flex items-center gap-2 text-[13px] text-slate-700 mt-2">
          <input type="checkbox" checked={available} onChange={(e) => setAvailable(e.target.checked)} className="rounded" />
          Available for customers to shop
        </label>
      </div>
    </Modal>
  );
}

const inputCls  = 'w-full px-3 py-2 text-[14px] bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400';
const selectCls = inputCls + ' appearance-none';

function Labeled({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      {hint && <span className="text-[11px] text-slate-400 ml-1">· {hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
